<template>
  <canvas ref="gradientCanvas" id="gradient-canvas"></canvas>
</template>

<script setup lang="ts">
import { ref, onMounted, onBeforeUnmount, type Ref } from 'vue';

// Core TypeScript interfaces and types
interface UniformConfig {
  value?: any;
  type?: string;
  excludeFrom?: string;
  transpose?: boolean;
}

interface MaterialUniforms {
  [key: string]: UniformInstance;
}

interface UniformInstance {
  type: string;
  value: any;
  typeFn: string;
  excludeFrom?: string;
  transpose?: boolean;
  update(location?: WebGLUniformLocation): void;
  getDeclaration(name: string, type: string, length?: number): string;
}

interface AttributeConfig {
  target: number;
  size: number;
  type?: number;
  normalized?: boolean;
}

interface AttributeInstance {
  target: number;
  size: number;
  type: number;
  normalized: boolean;
  buffer: WebGLBuffer;
  values?: Float32Array | Uint16Array;
  update(): void;
  attach(name: string, program: WebGLProgram): number;
  use(location: number): void;
}

interface GeometryAttributes {
  position: AttributeInstance;
  uv: AttributeInstance;
  uvNorm: AttributeInstance;
  index: AttributeInstance;
}

interface PlaneGeometryInstance {
  attributes: GeometryAttributes;
  xSegCount: number;
  ySegCount: number;
  vertexCount: number;
  quadCount: number;
  width: number;
  height: number;
  orientation: string;
  setTopology(xSegs?: number, ySegs?: number): void;
  setSize(width?: number, height?: number, orientation?: string): void;
}

interface MaterialInstance {
  uniforms: MaterialUniforms;
  uniformInstances: Array<{ uniform: UniformInstance; location: WebGLUniformLocation | null }>;
  vertexSource: string;
  Source: string;
  vertexShader: WebGLShader;
  fragmentShader: WebGLShader;
  program: WebGLProgram;
  attachUniforms(name: string | undefined, uniforms: any): void;
}

interface MeshInstance {
  geometry: PlaneGeometryInstance;
  material: MaterialInstance;
  wireframe: boolean;
  attributeInstances: Array<{ attribute: AttributeInstance; location: number }>;
  draw(): void;
  remove(): void;
}

interface CommonUniforms {
  projectionMatrix: UniformInstance;
  modelViewMatrix: UniformInstance;
  resolution: UniformInstance;
  aspectRatio: UniformInstance;
}

interface ShaderFiles {
  vertex: string;
  noise: string;
  blend: string;
  fragment: string;
}

interface GradientConfig {
  presetName: string;
  wireframe: boolean;
  density: [number, number];
  zoom: number;
  rotation: number;
  playing: boolean;
}

interface GradientUniforms extends MaterialUniforms {
  u_time: UniformInstance;
  u_shadow_power: UniformInstance;
  u_darken_top: UniformInstance;
  u_active_colors: UniformInstance;
  u_global: UniformInstance;
  u_vertDeform: UniformInstance;
  u_baseColor: UniformInstance;
  u_waveLayers: UniformInstance;
}

type NormalizedColor = [number, number, number];

// Reactive refs
const gradientCanvas: Ref<HTMLCanvasElement | null> = ref(null);
const gradient: Ref<any | null> = ref(null);

// Lifecycle hooks
onMounted(() => {
  initGradient();
});

onBeforeUnmount(() => {
  if (gradient.value) {
    gradient.value.disconnect();
  }
});

// Methods
const initGradient = (): void => {
  if (!gradientCanvas.value) return;

  gradient.value = new Gradient();
  gradient.value.initGradient('#gradient-canvas');
};

// Public methods for external control
const play = (): void => {
  if (gradient.value) {
    gradient.value.play();
  }
};

const pause = (): void => {
  if (gradient.value) {
    gradient.value.pause();
  }
};

// Expose methods for template refs
defineExpose({
  play,
  pause,
});

// Utility function for color normalization
function normalizeColor(hexCode: number): NormalizedColor {
  return [((hexCode >> 16) & 255) / 255, ((hexCode >> 8) & 255) / 255, (255 & hexCode) / 255];
}

// Utility function for setting initial properties (TypeScript version of original 'e' function)
function setProperty<T extends object, K extends keyof T>(object: T, propertyName: K, val: T[K]): T[K] {
  if (propertyName in object) {
    Object.defineProperty(object, propertyName, {
      value: val,
      enumerable: true,
      configurable: true,
      writable: true,
    });
  } else {
    object[propertyName] = val;
  }
  return object[propertyName];
}

// Essential functionality of WebGL
class MiniGl {
  canvas: HTMLCanvasElement;
  gl: WebGLRenderingContext;
  meshes: MeshInstance[];
  lastDebugMsg: Date | undefined;
  debug!: (...args: any[]) => void;
  Material!: new (vertexShader: string, fragmentShader: string, uniforms?: MaterialUniforms) => MaterialInstance;
  Uniform!: new (config: UniformConfig) => UniformInstance;
  PlaneGeometry!: new (
    width?: number,
    height?: number,
    xSegs?: number,
    ySegs?: number,
    orientation?: string
  ) => PlaneGeometryInstance;
  Mesh!: new (geometry: PlaneGeometryInstance, material: MaterialInstance) => MeshInstance;
  Attribute!: new (config: AttributeConfig) => AttributeInstance;
  commonUniforms!: CommonUniforms;
  width: number = 640;
  height: number = 480;

  constructor(canvas: HTMLCanvasElement, width: number | null, height: number | null, debug: boolean = false) {
    this.canvas = canvas;
    const gl = canvas.getContext('webgl', { antialias: true });
    if (!gl) {
      throw new Error('WebGL context not available');
    }
    this.gl = gl;
    this.meshes = [];

    this.setupDebug(debug);
    this.defineClasses();
    this.initializeCommonUniforms();

    if (width && height) {
      this.setSize(width, height);
    }
  }

  private setupDebug(debug: boolean): void {
    const debugOutput = document.location.search.toLowerCase().indexOf('debug=webgl') !== -1;
    this.lastDebugMsg = undefined;
    this.debug =
      debug && debugOutput
        ? (message: string, ...args: any[]) => {
            const currentTime = new Date();
            const timeDiff = this.lastDebugMsg ? +currentTime - +this.lastDebugMsg : Infinity;
            if (timeDiff > 1000) console.log('---');
            console.log(
              currentTime.toLocaleTimeString() + Array(Math.max(0, 32 - message.length)).join(' ') + message + ': ',
              ...args
            );
            this.lastDebugMsg = currentTime;
          }
        : () => {};
  }

  private defineClasses(): void {
    const _miniGl = this;
    const context = this.gl;

    this.Material = class implements MaterialInstance {
      uniforms: MaterialUniforms;
      uniformInstances: Array<{ uniform: UniformInstance; location: WebGLUniformLocation | null }> = [];
      vertexSource!: string;
      Source!: string;
      vertexShader!: WebGLShader;
      fragmentShader!: WebGLShader;
      program!: WebGLProgram;

      constructor(vertexShaders: string, fragments: string, uniforms: MaterialUniforms = {} as MaterialUniforms) {
        this.uniforms = uniforms;

        const getShaderByType = (type: number, source: string): WebGLShader => {
          const shader = context.createShader(type);
          if (!shader) {
            throw new Error('Failed to create shader');
          }
          context.shaderSource(shader, source);
          context.compileShader(shader);
          if (!context.getShaderParameter(shader, context.COMPILE_STATUS)) {
            console.error(context.getShaderInfoLog(shader));
          }
          _miniGl.debug('Material.compileShaderSource', { source });
          return shader;
        };

        const getUniformVariableDeclarations = (uniforms: any, type: string): string => {
          const isUniformObject = (val: any): boolean => {
            return val && typeof val === 'object';
          };

          const hasGetDeclaration = (val: any): boolean => {
            return typeof val.getDeclaration === 'function';
          };

          const getUniformType = (val: any): string => {
            return (isUniformObject(val) && val.type) || 'float';
          };

          const createUniformDeclaration = ([uniform, value]: [string, any]): string => {
            if (isUniformObject(value) && hasGetDeclaration(value)) {
              return value.getDeclaration(uniform, type);
            }
            const uniformType = getUniformType(value);
            return `uniform ${uniformType} ${uniform};`;
          };

          return Object.entries(uniforms).map(createUniformDeclaration).join('\n');
        };

        const prefix = '\n              precision highp float;\n            ';
        this.vertexSource = `\n              ${prefix}\n              attribute vec4 position;\n              attribute vec2 uv;\n              attribute vec2 uvNorm;\n              ${getUniformVariableDeclarations(_miniGl.commonUniforms, 'vertex')}\n              ${getUniformVariableDeclarations(uniforms, 'vertex')}\n              ${vertexShaders}\n            `;
        this.Source = `\n              ${prefix}\n              ${getUniformVariableDeclarations(_miniGl.commonUniforms, 'fragment')}\n              ${getUniformVariableDeclarations(uniforms, 'fragment')}\n              ${fragments}\n            `;
        this.vertexShader = getShaderByType(context.VERTEX_SHADER, this.vertexSource);
        this.fragmentShader = getShaderByType(context.FRAGMENT_SHADER, this.Source);
        this.program = context.createProgram()!;
        context.attachShader(this.program, this.vertexShader);
        context.attachShader(this.program, this.fragmentShader);
        context.linkProgram(this.program);
        if (!context.getProgramParameter(this.program, context.LINK_STATUS)) {
          console.error(context.getProgramInfoLog(this.program));
        }
        context.useProgram(this.program);
        this.attachUniforms(undefined, _miniGl.commonUniforms);
        this.attachUniforms(undefined, this.uniforms);
      }

      attachUniforms(name: string | undefined, uniforms: any): void {
        if (name === undefined) {
          Object.entries(uniforms).forEach(([uniformName, uniform]) => {
            this.attachUniforms(uniformName, uniform);
          });
        } else if (uniforms.type === 'array') {
          uniforms.value.forEach((uniform: any, i: number) => this.attachUniforms(`${name}[${i}]`, uniform));
        } else if (uniforms.type === 'struct') {
          Object.entries(uniforms.value).forEach(([uniformName, uniform]) =>
            this.attachUniforms(`${name}.${uniformName}`, uniform)
          );
        } else {
          _miniGl.debug('Material.attachUniforms', {
            name: name,
            uniform: uniforms,
          });
          this.uniformInstances.push({
            uniform: uniforms,
            location: context.getUniformLocation(this.program, name),
          });
        }
      }
    };

    this.Uniform = class implements UniformInstance {
      type: string = 'float';
      value: any;
      typeFn: string;
      excludeFrom?: string;
      transpose?: boolean;

      constructor(config: UniformConfig) {
        Object.assign(this, config);
        this.typeFn =
          (
            {
              float: '1f',
              int: '1i',
              vec2: '2fv',
              vec3: '3fv',
              vec4: '4fv',
              mat4: 'Matrix4fv',
            } as Record<string, string>
          )[this.type] || '1f';
      }

      update(location?: WebGLUniformLocation): void {
        if (this.value !== undefined && location) {
          const isMatrix = this.typeFn.indexOf('Matrix') === 0;
          const method = `uniform${this.typeFn}` as keyof WebGLRenderingContext;
          if (isMatrix) {
            (context[method] as any)(location, this.transpose || false, this.value);
          } else {
            (context[method] as any)(location, this.value);
          }
        }
      }

      getDeclaration(name: string, type: string, length?: number): string {
        if (this.excludeFrom === type) return '';

        if (this.type === 'array') {
          return (
            this.value[0].getDeclaration(name, type, this.value.length) +
            `\nconst int ${name}_length = ${this.value.length};`
          );
        }

        if (this.type === 'struct') {
          let nameNoPrefix = name.replace('u_', '');
          nameNoPrefix = nameNoPrefix.charAt(0).toUpperCase() + nameNoPrefix.slice(1);
          const structBody = Object.entries(this.value)
            .map(([fieldName, uniform]: [string, any]) =>
              uniform.getDeclaration(fieldName, type).replace(/^uniform/, '')
            )
            .join('');
          return `uniform struct ${nameNoPrefix} {\n${structBody}\n} ${name}${length && length > 0 ? `[${length}]` : ''};`;
        }

        return `uniform ${this.type} ${name}${length && length > 0 ? `[${length}]` : ''};`;
      }
    };

    this.PlaneGeometry = class implements PlaneGeometryInstance {
      attributes: GeometryAttributes;
      xSegCount: number = 1;
      ySegCount: number = 1;
      vertexCount: number = 4;
      quadCount: number = 2;
      width: number = 1;
      height: number = 1;
      orientation: string = 'xz';

      constructor(width?: number, height?: number, xSegs?: number, ySegs?: number, orientation?: string) {
        context.createBuffer();
        this.attributes = {
          position: new _miniGl.Attribute({
            target: context.ARRAY_BUFFER,
            size: 3,
          }),
          uv: new _miniGl.Attribute({
            target: context.ARRAY_BUFFER,
            size: 2,
          }),
          uvNorm: new _miniGl.Attribute({
            target: context.ARRAY_BUFFER,
            size: 2,
          }),
          index: new _miniGl.Attribute({
            target: context.ELEMENT_ARRAY_BUFFER,
            size: 3,
            type: context.UNSIGNED_SHORT,
          }),
        };
        this.setTopology(xSegs, ySegs);
        this.setSize(width, height, orientation);
      }

      setTopology(xSegs: number = 1, ySegs: number = 1): void {
        this.xSegCount = xSegs;
        this.ySegCount = ySegs;
        this.vertexCount = (this.xSegCount + 1) * (this.ySegCount + 1);
        this.quadCount = this.xSegCount * this.ySegCount * 2;

        this.attributes.uv.values = new Float32Array(2 * this.vertexCount);
        this.attributes.uvNorm.values = new Float32Array(2 * this.vertexCount);
        this.attributes.index.values = new Uint16Array(3 * this.quadCount);

        for (let y = 0; y <= this.ySegCount; y++) {
          for (let x = 0; x <= this.xSegCount; x++) {
            const i = y * (this.xSegCount + 1) + x;

            this.attributes.uv.values![2 * i] = x / this.xSegCount;
            this.attributes.uv.values![2 * i + 1] = 1 - y / this.ySegCount;
            this.attributes.uvNorm.values![2 * i] = (x / this.xSegCount) * 2 - 1;
            this.attributes.uvNorm.values![2 * i + 1] = 1 - (y / this.ySegCount) * 2;

            if (x < this.xSegCount && y < this.ySegCount) {
              const s = y * this.xSegCount + x;
              const indices = this.attributes.index.values as Uint16Array;
              indices[6 * s] = i;
              indices[6 * s + 1] = i + 1 + this.xSegCount;
              indices[6 * s + 2] = i + 1;
              indices[6 * s + 3] = i + 1;
              indices[6 * s + 4] = i + 1 + this.xSegCount;
              indices[6 * s + 5] = i + 2 + this.xSegCount;
            }
          }
        }

        this.attributes.uv.update();
        this.attributes.uvNorm.update();
        this.attributes.index.update();

        _miniGl.debug('Geometry.setTopology', {
          uv: this.attributes.uv,
          uvNorm: this.attributes.uvNorm,
          index: this.attributes.index,
        });
      }

      setSize(width: number = 1, height: number = 1, orientation: string = 'xz'): void {
        this.width = width;
        this.height = height;
        this.orientation = orientation;

        if (!this.attributes.position.values || this.attributes.position.values.length !== 3 * this.vertexCount) {
          this.attributes.position.values = new Float32Array(3 * this.vertexCount);
        }

        const startX = width / -2;
        const startY = height / -2;
        const segmentWidth = width / this.xSegCount;
        const segmentHeight = height / this.ySegCount;

        for (let yIndex = 0; yIndex <= this.ySegCount; yIndex++) {
          const posY = startY + yIndex * segmentHeight;
          for (let xIndex = 0; xIndex <= this.xSegCount; xIndex++) {
            const posX = startX + xIndex * segmentWidth;
            const vertexIndex = yIndex * (this.xSegCount + 1) + xIndex;
            const positions = this.attributes.position.values as Float32Array;

            positions[3 * vertexIndex + 'xyz'.indexOf(orientation[0])] = posX;
            positions[3 * vertexIndex + 'xyz'.indexOf(orientation[1])] = -posY;
          }
        }

        this.attributes.position.update();
        _miniGl.debug('Geometry.setSize', {
          position: this.attributes.position,
        });
      }
    };

    this.Mesh = class implements MeshInstance {
      geometry: PlaneGeometryInstance;
      material: MaterialInstance;
      wireframe: boolean = false;
      attributeInstances: Array<{ attribute: AttributeInstance; location: number }> = [];

      constructor(geometry: PlaneGeometryInstance, material: MaterialInstance) {
        this.geometry = geometry;
        this.material = material;

        Object.entries(this.geometry.attributes).forEach(([name, attribute]) => {
          this.attributeInstances.push({
            attribute: attribute,
            location: attribute.attach(name, this.material.program),
          });
        });

        _miniGl.meshes.push(this);
        _miniGl.debug('Mesh.constructor', { mesh: this });
      }

      draw(): void {
        context.useProgram(this.material.program);

        this.material.uniformInstances.forEach(({ uniform, location }) => uniform.update(location || undefined));

        this.attributeInstances.forEach(({ attribute, location }) => attribute.use(location));

        const drawMode = this.wireframe ? context.LINES : context.TRIANGLES;
        context.drawElements(drawMode, this.geometry.attributes.index.values!.length, context.UNSIGNED_SHORT, 0);
      }

      remove(): void {
        _miniGl.meshes = _miniGl.meshes.filter(mesh => mesh !== this);
      }
    };

    this.Attribute = class implements AttributeInstance {
      target!: number;
      size!: number;
      type: number = context.FLOAT;
      normalized: boolean = false;
      buffer: WebGLBuffer;
      values?: Float32Array | Uint16Array;

      constructor(config: AttributeConfig) {
        this.buffer = context.createBuffer()!;
        Object.assign(this, config);
        this.update();
      }

      update(): void {
        if (this.values !== undefined) {
          context.bindBuffer(this.target, this.buffer);
          context.bufferData(this.target, this.values, context.STATIC_DRAW);
        }
      }

      attach(name: string, program: WebGLProgram): number {
        const location = context.getAttribLocation(program, name);
        if (this.target === context.ARRAY_BUFFER && location >= 0) {
          context.enableVertexAttribArray(location);
          context.vertexAttribPointer(location, this.size, this.type, this.normalized, 0, 0);
        }
        return location;
      }

      use(location: number): void {
        context.bindBuffer(this.target, this.buffer);
        if (this.target === context.ARRAY_BUFFER && location >= 0) {
          context.enableVertexAttribArray(location);
          context.vertexAttribPointer(location, this.size, this.type, this.normalized, 0, 0);
        }
      }
    };
  }

  private initializeCommonUniforms(): void {
    const identityMatrix = [1, 0, 0, 0, 0, 1, 0, 0, 0, 0, 1, 0, 0, 0, 0, 1];
    this.commonUniforms = {
      projectionMatrix: new this.Uniform({
        type: 'mat4',
        value: identityMatrix,
      }),
      modelViewMatrix: new this.Uniform({
        type: 'mat4',
        value: identityMatrix,
      }),
      resolution: new this.Uniform({
        type: 'vec2',
        value: [1, 1],
      }),
      aspectRatio: new this.Uniform({
        type: 'float',
        value: 1,
      }),
    };
  }

  setSize(width: number = 640, height: number = 480): void {
    this.width = width;
    this.height = height;
    this.canvas.width = width;
    this.canvas.height = height;
    this.gl.viewport(0, 0, width, height);
    this.commonUniforms.resolution.value = [width, height];
    this.commonUniforms.aspectRatio.value = width / height;
    this.debug('MiniGL.setSize', { width, height });
  }

  setOrthographicCamera(
    left: number = 0,
    right: number = 0,
    top: number = 0,
    near: number = -2000,
    far: number = 2000
  ): void {
    this.commonUniforms.projectionMatrix.value = [
      2 / this.width,
      0,
      0,
      0,
      0,
      2 / this.height,
      0,
      0,
      0,
      0,
      2 / (near - far),
      0,
      left,
      right,
      top,
      1,
    ];
    this.debug('setOrthographicCamera', this.commonUniforms.projectionMatrix.value);
  }

  render(): void {
    this.gl.clearColor(0, 0, 0, 0);
    this.gl.clearDepth(1);
    this.gl.clear(this.gl.COLOR_BUFFER_BIT | this.gl.DEPTH_BUFFER_BIT);
    this.meshes.forEach(mesh => mesh.draw());
  }
}

// Gradient object
class Gradient {
  el?: HTMLCanvasElement | null = undefined;
  cssVarRetries: number = 0;
  maxCssVarRetries: number = 200;
  angle: number = 0;
  isLoadedClass: boolean = false;
  isScrolling: boolean = false;
  scrollingTimeout?: number = undefined;
  scrollingRefreshDelay: number = 200;
  isIntersecting: boolean = false;
  shaderFiles?: ShaderFiles = undefined;
  vertexShader?: string = undefined;
  sectionColors?: NormalizedColor[] = undefined;
  computedCanvasStyle?: CSSStyleDeclaration = undefined;
  conf?: GradientConfig = undefined;
  uniforms?: GradientUniforms = undefined;
  t: number = 1253106;
  last: number = 0;
  width?: number = undefined;
  minWidth: number = 1111;
  height: number = 600;
  xSegCount?: number = undefined;
  ySegCount?: number = undefined;
  mesh?: MeshInstance = undefined;
  material?: MaterialInstance = undefined;
  geometry?: PlaneGeometryInstance = undefined;
  minigl?: MiniGl = undefined;
  scrollObserver?: any = undefined;
  amp: number = 320;
  seed: number = 5;
  freqX: number = 14e-5;
  freqY: number = 29e-5;
  freqDelta: number = 1e-5;
  activeColors: number[] = [1, 1, 1, 1];
  isMetaKey: boolean = false;
  isGradientLegendVisible: boolean = false;
  isMouseDown: boolean = false;
  isStatic?: boolean = undefined;

  // Event handler properties (need to be declared as arrow functions for 'this' binding)
  handleScroll: () => void;
  handleScrollEnd: () => void;
  resize: () => void;
  handleMouseDown: (event: MouseEvent) => void;
  handleMouseUp: () => void;
  animate: (currentTime: number) => void;
  addIsLoadedClass: () => void;
  pause: () => void;
  play: () => void;
  initGradient: (selector: string) => Gradient;

  constructor() {
    // Initialize event handlers
    this.handleScroll = () => {
      clearTimeout(this.scrollingTimeout);
      this.scrollingTimeout = window.setTimeout(this.handleScrollEnd, this.scrollingRefreshDelay);
      if (this.isGradientLegendVisible) this.hideGradientLegend();
      if (this.conf?.playing) {
        this.isScrolling = true;
        this.pause();
      }
    };

    this.handleScrollEnd = () => {
      this.isScrolling = false;
      if (this.isIntersecting) this.play();
    };

    this.resize = () => {
      if (!this.minigl || !this.mesh || !this.conf) return;
      this.width = window.innerWidth;
      this.minigl.setSize(this.width, this.height);
      this.minigl.setOrthographicCamera();
      this.xSegCount = Math.ceil(this.width * this.conf.density[0]);
      this.ySegCount = Math.ceil(this.height * this.conf.density[1]);
      this.mesh.geometry.setTopology(this.xSegCount, this.ySegCount);
      this.mesh.geometry.setSize(this.width, this.height);
      if (this.mesh.material.uniforms.u_shadow_power) {
        this.mesh.material.uniforms.u_shadow_power.value = this.width < 600 ? 5 : 6;
      }
    };

    this.handleMouseDown = (event: MouseEvent) => {
      if (this.isGradientLegendVisible) {
        this.isMetaKey = event.metaKey;
        this.isMouseDown = true;
        if (this.conf?.playing === false) {
          requestAnimationFrame(this.animate);
        }
      }
    };

    this.handleMouseUp = () => {
      this.isMouseDown = false;
    };

    this.animate = (currentTime: number) => {
      if (!this.shouldSkipFrame(currentTime) || this.isMouseDown) {
        this.t += Math.min(currentTime - this.last, 1000 / 15);
        this.last = currentTime;

        if (this.isMouseDown) {
          let timeIncrement = 160;
          if (this.isMetaKey) timeIncrement = -160;
          this.t += timeIncrement;
        }

        if (this.mesh?.material.uniforms.u_time) {
          this.mesh.material.uniforms.u_time.value = this.t;
        }

        this.minigl?.render();
      }

      if (this.last !== 0 && this.isStatic) {
        this.minigl?.render();
        this.disconnect();
        return;
      }

      if (this.conf?.playing || this.isMouseDown) {
        requestAnimationFrame(this.animate);
      }
    };

    this.addIsLoadedClass = () => {
      if (!this.isLoadedClass && this.el) {
        this.isLoadedClass = true;
        this.el.classList.add('isLoaded');
        setTimeout(() => {
          this.el?.parentElement?.classList.add('isLoaded');
        }, 3000);
      }
    };

    this.pause = () => {
      if (this.conf) this.conf.playing = false;
    };

    this.play = () => {
      requestAnimationFrame(this.animate);
      if (this.conf) this.conf.playing = true;
    };

    this.initGradient = (selector: string) => {
      this.el = document.querySelector(selector) as HTMLCanvasElement;
      this.connect();
      return this;
    };
  }

  async connect(): Promise<void> {
    // Create vertex shader with tuned mouse interaction parameters
    const vertexShader = `varying vec3 v_color;

void main() {
  float time = u_time * u_global.noiseSpeed;

  vec2 noiseCoord = resolution * uvNorm * u_global.noiseFreq;

  vec2 st = 1. - uvNorm.xy;


  //
  // Tilting the plane
  //

  // Front-to-back tilt
  float tilt = resolution.y / 2.0 * uvNorm.y;

  // Left-to-right angle
  float incline = resolution.x * uvNorm.x / 2.0 * u_vertDeform.incline;

  // Up-down shift to offset incline
  float offset = resolution.x / 2.0 * u_vertDeform.incline * mix(u_vertDeform.offsetBottom, u_vertDeform.offsetTop, uv.y);

  //
  // Vertex noise
  //

  float noise = snoise(vec3(
    noiseCoord.x * u_vertDeform.noiseFreq.x + time * u_vertDeform.noiseFlow,
    noiseCoord.y * u_vertDeform.noiseFreq.y,
    time * u_vertDeform.noiseSpeed + u_vertDeform.noiseSeed
  )) * u_vertDeform.noiseAmp;

  // Fade noise to zero at edges
  noise *= 1.0 - pow(abs(uvNorm.y), 2.0);

  // Clamp to 0
  noise = max(0.0, noise);

  vec3 pos = vec3(
    position.x,
    position.y + tilt + incline + noise - offset,
    position.z
  );

  //
  // Vertex color, to be passed to fragment shader
  //

  if (u_active_colors[0] == 1.) {
    v_color = u_baseColor;
  }

  for (int i = 0; i < u_waveLayers_length; i++) {
    if (u_active_colors[i + 1] == 1.) {
      WaveLayers layer = u_waveLayers[i];

      float layerNoise = smoothstep(
        layer.noiseFloor,
        layer.noiseCeil,
        snoise(vec3(
          noiseCoord.x * layer.noiseFreq.x + time * layer.noiseFlow,
          noiseCoord.y * layer.noiseFreq.y,
          time * layer.noiseSpeed + layer.noiseSeed
        )) / 2.0 + 0.5
      );

      v_color = blendNormal(v_color, layer.color, pow(layerNoise, 4.));
    }
  }

  //
  // Finish
  //

  gl_Position = projectionMatrix * modelViewMatrix * vec4(pos, 1.0);
}`;

    this.shaderFiles = {
      vertex: vertexShader,
      noise:
        '//\n// Description : Array and textureless GLSL 2D/3D/4D simplex\n//               noise functions.\n//      Author : Ian McEwan, Ashima Arts.\n//  Maintainer : stegu\n//     Lastmod : 20110822 (ijm)\n//     License : Copyright (C) 2011 Ashima Arts. All rights reserved.\n//               Distributed under the MIT License. See LICENSE file.\n//               https://github.com/ashima/webgl-noise\n//               https://github.com/stegu/webgl-noise\n//\n\nvec3 mod289(vec3 x) {\n  return x - floor(x * (1.0 / 289.0)) * 289.0;\n}\n\nvec4 mod289(vec4 x) {\n  return x - floor(x * (1.0 / 289.0)) * 289.0;\n}\n\nvec4 permute(vec4 x) {\n    return mod289(((x*34.0)+1.0)*x);\n}\n\nvec4 taylorInvSqrt(vec4 r)\n{\n  return 1.79284291400159 - 0.85373472095314 * r;\n}\n\nfloat snoise(vec3 v)\n{\n  const vec2  C = vec2(1.0/6.0, 1.0/3.0) ;\n  const vec4  D = vec4(0.0, 0.5, 1.0, 2.0);\n\n// First corner\n  vec3 i  = floor(v + dot(v, C.yyy) );\n  vec3 x0 =   v - i + dot(i, C.xxx) ;\n\n// Other corners\n  vec3 g = step(x0.yzx, x0.xyz);\n  vec3 l = 1.0 - g;\n  vec3 i1 = min( g.xyz, l.zxy );\n  vec3 i2 = max( g.xyz, l.zxy );\n\n  //   x0 = x0 - 0.0 + 0.0 * C.xxx;\n  //   x1 = x0 - i1  + 1.0 * C.xxx;\n  //   x2 = x0 - i2  + 2.0 * C.xxx;\n  //   x3 = x0 - 1.0 + 3.0 * C.xxx;\n  vec3 x1 = x0 - i1 + C.xxx;\n  vec3 x2 = x0 - i2 + C.yyy; // 2.0*C.x = 1/3 = C.y\n  vec3 x3 = x0 - D.yyy;      // -1.0+3.0*C.x = -0.5 = -D.y\n\n// Permutations\n  i = mod289(i);\n  vec4 p = permute( permute( permute(\n            i.z + vec4(0.0, i1.z, i2.z, 1.0 ))\n          + i.y + vec4(0.0, i1.y, i2.y, 1.0 ))\n          + i.x + vec4(0.0, i1.x, i2.x, 1.0 ));\n\n// Gradients: 7x7 points over a square, mapped onto an octahedron.\n// The ring size 17*17 = 289 is close to a multiple of 49 (49*6 = 294)\n  float n_ = 0.142857142857; // 1.0/7.0\n  vec3  ns = n_ * D.wyz - D.xzx;\n\n  vec4 j = p - 49.0 * floor(p * ns.z * ns.z);  //  mod(p,7*7)\n\n  vec4 x_ = floor(j * ns.z);\n  vec4 y_ = floor(j - 7.0 * x_ );    // mod(j,N)\n\n  vec4 x = x_ *ns.x + ns.yyyy;\n  vec4 y = y_ *ns.x + ns.yyyy;\n  vec4 h = 1.0 - abs(x) - abs(y);\n\n  vec4 b0 = vec4( x.xy, y.xy );\n  vec4 b1 = vec4( x.zw, y.zw );\n\n  //vec4 s0 = vec4(lessThan(b0,0.0))*2.0 - 1.0;\n  //vec4 s1 = vec4(lessThan(b1,0.0))*2.0 - 1.0;\n  vec4 s0 = floor(b0)*2.0 + 1.0;\n  vec4 s1 = floor(b1)*2.0 + 1.0;\n  vec4 sh = -step(h, vec4(0.0));\n\n  vec4 a0 = b0.xzyw + s0.xzyw*sh.xxyy ;\n  vec4 a1 = b1.xzyw + s1.xzyw*sh.zzww ;\n\n  vec3 p0 = vec3(a0.xy,h.x);\n  vec3 p1 = vec3(a0.zw,h.y);\n  vec3 p2 = vec3(a1.xy,h.z);\n  vec3 p3 = vec3(a1.zw,h.w);\n\n//Normalise gradients\n  vec4 norm = taylorInvSqrt(vec4(dot(p0,p0), dot(p1,p1), dot(p2, p2), dot(p3,p3)));\n  p0 *= norm.x;\n  p1 *= norm.y;\n  p2 *= norm.z;\n  p3 *= norm.w;\n\n// Mix final noise value\n  vec4 m = max(0.6 - vec4(dot(x0,x0), dot(x1,x1), dot(x2,x2), dot(x3,x3)), 0.0);\n  m = m * m;\n  return 42.0 * dot( m*m, vec4( dot(p0,x0), dot(p1,x1),\n                                dot(p2,x2), dot(p3,x3) ) );\n}',
      blend:
        '//\n// https://github.com/jamieowen/glsl-blend\n//\n\n// Normal\n\nvec3 blendNormal(vec3 base, vec3 blend) {\n\treturn blend;\n}\n\nvec3 blendNormal(vec3 base, vec3 blend, float opacity) {\n\treturn (blendNormal(base, blend) * opacity + base * (1.0 - opacity));\n}\n\n// Screen\n\nfloat blendScreen(float base, float blend) {\n\treturn 1.0-((1.0-base)*(1.0-blend));\n}\n\nvec3 blendScreen(vec3 base, vec3 blend) {\n\treturn vec3(blendScreen(base.r,blend.r),blendScreen(base.g,blend.g),blendScreen(base.b,blend.b));\n}\n\nvec3 blendScreen(vec3 base, vec3 blend, float opacity) {\n\treturn (blendScreen(base, blend) * opacity + base * (1.0 - opacity));\n}\n\n// Multiply\n\nvec3 blendMultiply(vec3 base, vec3 blend) {\n\treturn base*blend;\n}\n\nvec3 blendMultiply(vec3 base, vec3 blend, float opacity) {\n\treturn (blendMultiply(base, blend) * opacity + base * (1.0 - opacity));\n}\n\n// Overlay\n\nfloat blendOverlay(float base, float blend) {\n\treturn base<0.5?(2.0*base*blend):(1.0-2.0*(1.0-base)*(1.0-blend));\n}\n\nvec3 blendOverlay(vec3 base, vec3 blend) {\n\treturn vec3(blendOverlay(base.r,blend.r),blendOverlay(base.g,blend.g),blendOverlay(base.b,blend.b));\n}\n\nvec3 blendOverlay(vec3 base, vec3 blend, float opacity) {\n\treturn (blendOverlay(base, blend) * opacity + base * (1.0 - opacity));\n}\n\n// Hard light\n\nvec3 blendHardLight(vec3 base, vec3 blend) {\n\treturn blendOverlay(blend,base);\n}\n\nvec3 blendHardLight(vec3 base, vec3 blend, float opacity) {\n\treturn (blendHardLight(base, blend) * opacity + base * (1.0 - opacity));\n}\n\n// Soft light\n\nfloat blendSoftLight(float base, float blend) {\n\treturn (blend<0.5)?(2.0*base*blend+base*base*(1.0-2.0*blend)):(sqrt(base)*(2.0*blend-1.0)+2.0*base*(1.0-blend));\n}\n\nvec3 blendSoftLight(vec3 base, vec3 blend) {\n\treturn vec3(blendSoftLight(base.r,blend.r),blendSoftLight(base.g,blend.g),blendSoftLight(base.b,blend.b));\n}\n\nvec3 blendSoftLight(vec3 base, vec3 blend, float opacity) {\n\treturn (blendSoftLight(base, blend) * opacity + base * (1.0 - opacity));\n}\n\n// Color dodge\n\nfloat blendColorDodge(float base, float blend) {\n\treturn (blend==1.0)?blend:min(base/(1.0-blend),1.0);\n}\n\nvec3 blendColorDodge(vec3 base, vec3 blend) {\n\treturn vec3(blendColorDodge(base.r,blend.r),blendColorDodge(base.g,blend.g),blendColorDodge(base.b,blend.b));\n}\n\nvec3 blendColorDodge(vec3 base, vec3 blend, float opacity) {\n\treturn (blendColorDodge(base, blend) * opacity + base * (1.0 - opacity));\n}\n\n// Color burn\n\nfloat blendColorBurn(float base, float blend) {\n\treturn (blend==0.0)?blend:max((1.0-((1.0-base)/blend)),0.0);\n}\n\nvec3 blendColorBurn(vec3 base, vec3 blend) {\n\treturn vec3(blendColorBurn(base.r,blend.r),blendColorBurn(base.g,blend.g),blendColorBurn(base.b,blend.b));\n}\n\nvec3 blendColorBurn(vec3 base, vec3 blend, float opacity) {\n\treturn (blendColorBurn(base, blend) * opacity + base * (1.0 - opacity));\n}\n\n// Vivid Light\n\nfloat blendVividLight(float base, float blend) {\n\treturn (blend<0.5)?blendColorBurn(base,(2.0*blend)):blendColorDodge(base,(2.0*(blend-0.5)));\n}\n\nvec3 blendVividLight(vec3 base, vec3 blend) {\n\treturn vec3(blendVividLight(base.r,blend.r),blendVividLight(base.g,blend.g),blendVividLight(base.b,blend.b));\n}\n\nvec3 blendVividLight(vec3 base, vec3 blend, float opacity) {\n\treturn (blendVividLight(base, blend) * opacity + base * (1.0 - opacity));\n}\n\n// Lighten\n\nfloat blendLighten(float base, float blend) {\n\treturn max(blend,base);\n}\n\nvec3 blendLighten(vec3 base, vec3 blend) {\n\treturn vec3(blendLighten(base.r,blend.r),blendLighten(base.g,blend.g),blendLighten(base.b,blend.b));\n}\n\nvec3 blendLighten(vec3 base, vec3 blend, float opacity) {\n\treturn (blendLighten(base, blend) * opacity + base * (1.0 - opacity));\n}\n\n// Linear burn\n\nfloat blendLinearBurn(float base, float blend) {\n\t// Note : Same implementation as BlendSubtractf\n\treturn max(base+blend-1.0,0.0);\n}\n\nvec3 blendLinearBurn(vec3 base, vec3 blend) {\n\t// Note : Same implementation as BlendSubtract\n\treturn max(base+blend-vec3(1.0),vec3(0.0));\n}\n\nvec3 blendLinearBurn(vec3 base, vec3 blend, float opacity) {\n\treturn (blendLinearBurn(base, blend) * opacity + base * (1.0 - opacity));\n}\n\n// Linear dodge\n\nfloat blendLinearDodge(float base, float blend) {\n\t// Note : Same implementation as BlendAddf\n\treturn min(base+blend,1.0);\n}\n\nvec3 blendLinearDodge(vec3 base, vec3 blend) {\n\t// Note : Same implementation as BlendAdd\n\treturn min(base+blend,vec3(1.0));\n}\n\nvec3 blendLinearDodge(vec3 base, vec3 blend, float opacity) {\n\treturn (blendLinearDodge(base, blend) * opacity + base * (1.0 - opacity));\n}\n\n// Linear light\n\nfloat blendLinearLight(float base, float blend) {\n\treturn blend<0.5?blendLinearBurn(base,(2.0*blend)):blendLinearDodge(base,(2.0*(blend-0.5)));\n}\n\nvec3 blendLinearLight(vec3 base, vec3 blend) {\n\treturn vec3(blendLinearLight(base.r,blend.r),blendLinearLight(base.g,blend.g),blendLinearLight(base.b,blend.b));\n}\n\nvec3 blendLinearLight(vec3 base, vec3 blend, float opacity) {\n\treturn (blendLinearLight(base, blend) * opacity + base * (1.0 - opacity));\n}',
      fragment:
        'varying vec3 v_color;\n\nvoid main() {\n  vec3 color = v_color;\n  if (u_darken_top == 1.0) {\n    vec2 st = gl_FragCoord.xy/resolution.xy;\n    color.g -= pow(st.y + sin(-12.0) * st.x, u_shadow_power) * 0.4;\n  }\n  gl_FragColor = vec4(color, 1.0);\n}',
    };
    this.conf = {
      presetName: '',
      wireframe: false,
      density: [0.06, 0.16],
      zoom: 1,
      rotation: 0,
      playing: true,
    };

    if (document.querySelectorAll('canvas').length < 1) {
      console.log('DID NOT LOAD HERO STRIPE CANVAS');
    } else {
      this.minigl = new MiniGl(this.el!, null, null, true);
      requestAnimationFrame(() => {
        if (this.el) {
          this.computedCanvasStyle = getComputedStyle(this.el);
          this.waitForCssVars();
        }
      });
    }
  }

  disconnect(): void {
    if (this.scrollObserver) {
      window.removeEventListener('scroll', this.handleScroll);
      window.removeEventListener('mousedown', this.handleMouseDown);
      window.removeEventListener('mouseup', this.handleMouseUp);
      this.scrollObserver.disconnect();
    }
    window.removeEventListener('resize', this.resize);
  }

  initMaterial(): MaterialInstance | null {
    if (!this.minigl || !this.sectionColors) return null;

    this.uniforms = {
      u_time: new this.minigl.Uniform({
        value: 0,
      }),
      u_shadow_power: new this.minigl.Uniform({
        value: 10,
      }),
      u_darken_top: new this.minigl.Uniform({
        value: this.el?.dataset.jsDarkenTop === '' ? 1 : 0,
      }),
      u_active_colors: new this.minigl.Uniform({
        value: this.activeColors,
        type: 'vec4',
      }),
      u_global: new this.minigl.Uniform({
        value: {
          noiseFreq: new this.minigl.Uniform({
            value: [this.freqX, this.freqY],
            type: 'vec2',
          }),
          noiseSpeed: new this.minigl.Uniform({
            value: 5e-6,
          }),
        },
        type: 'struct',
      }),
      u_vertDeform: new this.minigl.Uniform({
        value: {
          incline: new this.minigl.Uniform({
            value: Math.sin(this.angle) / Math.cos(this.angle),
          }),
          offsetTop: new this.minigl.Uniform({
            value: -0.5,
          }),
          offsetBottom: new this.minigl.Uniform({
            value: -0.5,
          }),
          noiseFreq: new this.minigl.Uniform({
            value: [3, 4],
            type: 'vec2',
          }),
          noiseAmp: new this.minigl.Uniform({
            value: this.amp,
          }),
          noiseSpeed: new this.minigl.Uniform({
            value: 10,
          }),
          noiseFlow: new this.minigl.Uniform({
            value: 3,
          }),
          noiseSeed: new this.minigl.Uniform({
            value: this.seed,
          }),
        },
        type: 'struct',
        excludeFrom: 'fragment',
      }),
      u_baseColor: new this.minigl.Uniform({
        value: this.sectionColors[0],
        type: 'vec3',
        excludeFrom: 'fragment',
      }),
      u_waveLayers: new this.minigl.Uniform({
        value: [],
        excludeFrom: 'fragment',
        type: 'array',
      }),
    };

    for (let e = 1; e < this.sectionColors.length; e += 1) {
      this.uniforms.u_waveLayers.value.push(
        new this.minigl.Uniform({
          value: {
            color: new this.minigl.Uniform({
              value: this.sectionColors[e],
              type: 'vec3',
            }),
            noiseFreq: new this.minigl.Uniform({
              value: [2 + e / this.sectionColors.length, 3 + e / this.sectionColors.length],
              type: 'vec2',
            }),
            noiseSpeed: new this.minigl.Uniform({
              value: 11 + 0.3 * e,
            }),
            noiseFlow: new this.minigl.Uniform({
              value: 6.5 + 0.3 * e,
            }),
            noiseSeed: new this.minigl.Uniform({
              value: this.seed + 10 * e,
            }),
            noiseFloor: new this.minigl.Uniform({
              value: 0.1,
            }),
            noiseCeil: new this.minigl.Uniform({
              value: 0.63 + 0.07 * e,
            }),
          },
          type: 'struct',
        })
      );
    }

    this.vertexShader = [this.shaderFiles!.noise, this.shaderFiles!.blend, this.shaderFiles!.vertex].join('\n\n');
    return new this.minigl.Material(this.vertexShader, this.shaderFiles!.fragment, this.uniforms!);
  }

  initMesh(): void {
    if (!this.minigl) return;
    this.material = this.initMaterial() ?? undefined;
    if (!this.material) return;
    this.geometry = new this.minigl.PlaneGeometry();
    this.mesh = new this.minigl.Mesh(this.geometry, this.material);
  }

  shouldSkipFrame(currentTime: number): boolean {
    return !!window.document.hidden || !this.conf?.playing || parseInt(String(currentTime), 10) % 2 === 0;
  }

  updateFrequency(delta: number): void {
    this.freqX += delta;
    this.freqY += delta;
  }

  toggleColor(index: number): void {
    this.activeColors[index] = this.activeColors[index] === 0 ? 1 : 0;
  }

  showGradientLegend(): void {
    if (this.width! > this.minWidth) {
      this.isGradientLegendVisible = true;
      document.body.classList.add('isGradientLegendVisible');
    }
  }

  hideGradientLegend(): void {
    this.isGradientLegendVisible = false;
    document.body.classList.remove('isGradientLegendVisible');
  }

  init(): void {
    this.initGradientColors();
    this.initMesh();
    this.resize();
    requestAnimationFrame(this.animate);
    window.addEventListener('resize', this.resize);
  }

  waitForCssVars(): void {
    if (
      this.computedCanvasStyle &&
      this.computedCanvasStyle.getPropertyValue('--gradient-color-1').indexOf('#') !== -1
    ) {
      this.init();
      this.addIsLoadedClass();
    } else {
      this.cssVarRetries += 1;
      if (this.cssVarRetries > this.maxCssVarRetries) {
        this.sectionColors = [
          normalizeColor(16711680),
          normalizeColor(16711680),
          normalizeColor(16711935),
          normalizeColor(65280),
          normalizeColor(255),
        ];
        this.init();
        return;
      }
      requestAnimationFrame(() => this.waitForCssVars());
    }
  }

  initGradientColors(): void {
    if (!this.computedCanvasStyle) return;

    this.sectionColors = [
      '--gradient-color-1',
      '--gradient-color-2',
      '--gradient-color-3',
      '--gradient-color-4',
      '--gradient-color-5',
      '--gradient-color-6',
      '--gradient-color-7',
      '--gradient-color-8',
      '--gradient-color-9',
    ]
      .map(cssPropertyName => {
        let hex = this.computedCanvasStyle!.getPropertyValue(cssPropertyName).trim();
        // Check if shorthand hex value was used and double the length so the conversion in normalizeColor will work.
        if (hex.length === 4) {
          const hexTemp = hex
            .substr(1)
            .split('')
            .map(char => char + char)
            .join('');
          hex = `#${hexTemp}`;
        }
        return hex ? `0x${hex.substr(1)}` : null;
      })
      .filter((hex): hex is string => Boolean(hex))
      .map(hex => normalizeColor(parseInt(hex, 16)));
  }
}
</script>

<style lang="less">
#gradient-canvas {
  --gradient-color-1: #471571;
  --gradient-color-2: #7c229e;
  --gradient-color-3: #b02290;
  --gradient-color-4: #e92f6f;
  --gradient-color-5: #f6484f;
  --gradient-color-6: #fbc831;
  --gradient-color-7: #f3960f;
  --gradient-color-8: #5fc4f9;
  --gradient-color-9: #ffffff;

  width: 100vw;
  height: 100vh;
  position: absolute;
  top: 0;
  left: 0;
  z-index: -1;
}
</style>
